import React, { useEffect, useState } from 'react';
import { userAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, ShieldCheck, X, UserCog } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';
import { useAuth } from '../../contexts/AuthContext';

// Pretty display labels for the role identifiers used on the backend.
const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  dispatcher: 'Dispatcher',
  production_operator: 'Production Operator',
  viewer: 'Viewer',
  accountant: 'Accountant',
};
// Friendly summary of what each role can do — surfaced in the Add/Edit dialog
// so the operator doesn't have to memorize the matrix.
const ROLE_HINTS = {
  admin: 'Full access including User Management. Reserve for owners.',
  manager: 'Full access to every operational tab. No User Management.',
  dispatcher: 'Orders, New Order, Stock, Transport, Dashboard. No deletes.',
  production_operator: 'Production Planner, Planning, Stock. Can revert lots.',
  viewer: 'Read-only access to every tab. No edits, no deletes.',
  accountant: 'Dealer CRM, Dealers, Reports, Dashboard. No deletes.',
};
const TAB_LABELS = {
  dashboard: 'Dashboard',
  new_order: 'New Order',
  orders: 'Orders',
  product_summary: 'Product Summary',
  products: 'Products',
  dealers: 'Dealers',
  dealer_crm: 'Dealer CRM',
  reports: 'Reports',
  transport: 'Transport',
  stock: 'Stock',
  planning: 'Planning',
  production: 'Production',
  users: 'Users',
};

// Color swatch per role for the small chip in the user list.
const ROLE_COLOR = {
  admin: { bg: '#2B5339', fg: 'white' },
  manager: { bg: '#1F6F8B', fg: 'white' },
  dispatcher: { bg: '#E89D4F', fg: 'white' },
  production_operator: { bg: '#6A5ACD', fg: 'white' },
  viewer: { bg: '#9CA3AF', fg: 'white' },
  accountant: { bg: '#A05A2C', fg: 'white' },
};

export const UsersTab = () => {
  const confirm = useConfirm();
  const { user: currentUser, impersonate } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], roles: [], tabs: [], actions: [], role_defaults: {} });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null or user object
  const [form, setForm] = useState({ phone_number: '', name: '', role: 'manager', active: true, permissions: null });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await userAPI.listStaff();
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Failed to load users: ${msg}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ phone_number: '', name: '', role: 'manager', active: true, permissions: null });
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      phone_number: u.phone_number || '',
      name: u.name || '',
      role: u.role || 'viewer',
      active: u.active !== false,
      // Carry the per-user override map (or null to fall back to role defaults).
      permissions: u.permissions || null,
    });
    setDialogOpen(true);
  };

  // Toggle a single (tab, action) cell. Lazily creates the override layer so
  // unset cells continue to inherit the role default until the admin actually
  // changes something.
  const togglePerm = (tab, action) => {
    setForm((prev) => {
      const next = { ...prev };
      const base = data.role_defaults?.[prev.role] || {};
      const effective = computeEffective(prev.role, prev.permissions, data.role_defaults);
      const current = !!(effective[tab] && effective[tab][action]);
      const newVal = !current;
      const overrides = { ...(prev.permissions || {}) };
      const tabOv = { ...(overrides[tab] || {}) };
      // If the new value matches the role default, drop the override key so we
      // keep storage minimal and the user falls back to the role rules.
      const defaultVal = !!((base[tab] || {})[action]);
      if (newVal === defaultVal) {
        delete tabOv[action];
      } else {
        tabOv[action] = newVal;
      }
      if (Object.keys(tabOv).length === 0) {
        delete overrides[tab];
      } else {
        overrides[tab] = tabOv;
      }
      next.permissions = Object.keys(overrides).length === 0 ? null : overrides;
      return next;
    });
  };

  const resetOverrides = () => {
    setForm((prev) => ({ ...prev, permissions: null }));
  };

  const submit = async () => {
    if (!form.phone_number.trim() || !form.name.trim()) {
      toast.error('Phone number and name are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await userAPI.updateStaff(editing.id, {
          name: form.name.trim(),
          role: form.role,
          active: form.active,
          permissions: form.permissions, // may be null to clear overrides
        });
        toast.success('User updated');
      } else {
        await userAPI.createStaff({
          phone_number: form.phone_number.trim(),
          name: form.name.trim(),
          role: form.role,
          active: form.active,
          permissions: form.permissions,
        });
        toast.success('User created — they can now log in via OTP');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Save failed: ${msg}`);
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    const ok = await confirm({
      title: 'Delete user?',
      description: `${u.name} (${ROLE_LABELS[u.role] || u.role}) will be permanently removed and can no longer log in.`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await userAPI.deleteStaff(u.id);
      toast.success(`Deleted ${u.name}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Delete failed: ${msg}`);
    }
  };

  // Generate an impersonation token for the target user and swap into their
  // session. The current admin token is preserved so the global banner's
  // "Return to admin" button can restore it without re-login.
  const startImpersonate = async (u) => {
    if (u.id === currentUser?.id) {
      toast.info("You're already logged in as this user");
      return;
    }
    const ok = await confirm({
      title: `View app as ${u.name}?`,
      description: `You'll see the app exactly as a ${ROLE_LABELS[u.role] || u.role} sees it. A banner at the top lets you return to your admin session anytime. Token expires in 1 hour.`,
      confirmText: 'View as user',
    });
    if (!ok) return;
    try {
      const res = await userAPI.impersonate(u.id);
      impersonate(res.data.token, res.data.user);
      toast.success(`Now viewing as ${u.name}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Impersonation failed: ${msg}`);
    }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#2B5339' }}>
            <ShieldCheck className="w-6 h-6" /> User Management
          </h2>
          <p className="text-sm mt-1" style={{ color: '#5C6B61' }}>
            Add staff accounts and assign roles. Use the permissions matrix to fine-tune what each user can do — overrides apply on top of the role default.
          </p>
        </div>
        <Button onClick={openAdd} style={{ background: '#2B5339' }} data-testid="users-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      <Card className="overflow-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr style={{ background: '#FBF9F6' }}>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Phone</th>
              <th className="p-2 text-left">Role</th>
              <th className="p-2 text-center">Active</th>
              <th className="p-2 text-center">Overrides</th>
              <th className="p-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>
            ) : (data.users || []).length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center" style={{ color: '#5C6B61' }}>No staff users yet.</td></tr>
            ) : data.users.map((u) => {
              const color = ROLE_COLOR[u.role] || ROLE_COLOR.viewer;
              const hasOverrides = u.permissions && Object.keys(u.permissions).length > 0;
              return (
                <tr key={u.id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="users-row">
                  <td className="p-2 font-medium">{u.name}</td>
                  <td className="p-2 font-mono text-xs">{u.phone_number}</td>
                  <td className="p-2">
                    <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: color.bg, color: color.fg }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    {u.active !== false
                      ? <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: '#EFF4EE', color: '#2B5339' }}>Active</span>
                      : <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: '#FFF1EC', color: '#C05C35' }}>Disabled</span>}
                  </td>
                  <td className="p-2 text-center">
                    {hasOverrides
                      ? <Badge variant="outline" title="This user has custom permission overrides on top of the role default" data-testid="users-row-overrides">Custom</Badge>
                      : <span className="text-xs" style={{ color: '#5C6B61' }}>Role default</span>}
                  </td>
                  <td className="p-2 text-center">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => startImpersonate(u)} disabled={u.id === currentUser?.id || u.active === false} title={u.id === currentUser?.id ? "You're already this user" : (u.active === false ? 'User is disabled' : `View app as ${u.name}`)} data-testid="users-impersonate-btn"><UserCog className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid="users-edit-btn"><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(u)} className="text-[#C05C35] border-[#C05C35] hover:bg-[#FFF1EC]" data-testid="users-delete-btn"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="users-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit User — ${editing.name}` : 'Add User'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="text-xs font-medium" style={{ color: '#5C6B61' }}>Phone</label>
              <Input
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                disabled={!!editing}
                placeholder="+919876543210"
                data-testid="users-form-phone"
              />
              {editing && <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Phone can't be changed — recreate the user instead.</p>}
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-medium" style={{ color: '#5C6B61' }}>Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ravi Kumar"
                data-testid="users-form-name"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs font-medium" style={{ color: '#5C6B61' }}>Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value, permissions: null })}
                className="w-full h-10 px-3 rounded-md border text-sm"
                style={{ borderColor: '#E2DFD8' }}
                data-testid="users-form-role"
              >
                {(data.roles || []).map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                ))}
              </select>
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>{ROLE_HINTS[form.role] || ''}</p>
            </div>
            <div className="sm:col-span-3 flex items-center gap-3">
              <label className="text-xs font-medium inline-flex items-center gap-2 cursor-pointer" style={{ color: '#5C6B61' }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="users-form-active"
                />
                Active (can log in)
              </label>
              <Button type="button" variant="outline" size="sm" onClick={resetOverrides} disabled={!form.permissions} data-testid="users-form-reset">
                <X className="w-3 h-3 mr-1" /> Reset all overrides to role default
              </Button>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm" style={{ color: '#2B5339' }}>Permissions matrix</h3>
              <p className="text-[11px]" style={{ color: '#5C6B61' }}>Click a cell to toggle. Cells matching the role default are shown lighter.</p>
            </div>
            <PermissionMatrix
              role={form.role}
              overrides={form.permissions}
              roleDefaults={data.role_defaults}
              tabs={data.tabs}
              actions={data.actions}
              onToggle={togglePerm}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="users-form-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} style={{ background: '#2B5339' }} data-testid="users-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Merges role defaults with the user-specific overrides on the client side so
// the matrix preview is responsive without a server round-trip.
const computeEffective = (role, overrides, roleDefaults) => {
  const base = (roleDefaults && roleDefaults[role]) || {};
  if (!overrides) {
    const out = {};
    Object.keys(base).forEach((t) => { out[t] = { ...base[t] }; });
    return out;
  }
  const out = {};
  Object.keys(base).forEach((t) => {
    out[t] = { ...base[t] };
    const tov = overrides[t] || {};
    Object.keys(tov).forEach((a) => { if (typeof tov[a] === 'boolean') out[t][a] = tov[a]; });
  });
  return out;
};

// Tab × Action grid. Cells lit when the action is granted. Hovered cells show
// what'd change. Overridden cells get a brighter color so the admin can spot
// at-a-glance where they've diverged from the role defaults.
const PermissionMatrix = ({ role, overrides, roleDefaults, tabs, actions, onToggle }) => {
  const eff = computeEffective(role, overrides, roleDefaults);
  return (
    <div className="overflow-x-auto rounded border" style={{ borderColor: '#E2DFD8' }} data-testid="users-perm-matrix">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: '#FBF9F6' }}>
            <th className="p-2 text-left">Tab</th>
            {(actions || []).map((a) => (
              <th key={a} className="p-2 text-center capitalize">{a}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(tabs || []).map((t) => (
            <tr key={t} className="border-t" style={{ borderColor: '#F1EEE8' }}>
              <td className="p-2 font-medium" style={{ color: '#2B5339' }}>{TAB_LABELS[t] || t}</td>
              {(actions || []).map((a) => {
                const val = !!(eff[t] && eff[t][a]);
                const defaultVal = !!(roleDefaults?.[role]?.[t]?.[a]);
                const isOverride = (overrides?.[t] && typeof overrides[t][a] === 'boolean');
                const bg = val
                  ? (isOverride ? '#2B5339' : '#A6CCB1')
                  : (isOverride ? '#FFD1C2' : '#F1EEE8');
                const fg = val ? 'white' : '#5C6B61';
                const symbol = val ? '✓' : '·';
                return (
                  <td key={a} className="p-1 text-center">
                    <button
                      type="button"
                      onClick={() => onToggle(t, a)}
                      className="w-8 h-8 rounded font-bold text-base"
                      style={{ background: bg, color: fg, border: isOverride ? '1px solid #2B5339' : '1px solid transparent' }}
                      title={`${val ? 'Allow' : 'Deny'} · ${isOverride ? 'override' : 'role default'} (default: ${defaultVal ? 'allow' : 'deny'})`}
                      data-testid={`users-perm-${t}-${a}`}
                    >
                      {symbol}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
